/**
 * HOW A MAN REACHED THE APPLICATION.
 *
 * `answers.source` already records FIRST TOUCH - the landing page of this
 * browser's first ever visit, days or weeks ago. That is a different fact from
 * the entry page: the page the Apply button was actually on. A man can land on
 * an article in July and click Apply from the Mentorship page in August, and
 * first touch answers only the first half of that.
 *
 * ENTRY PAGE COMES FROM THE REFERRER, not from tagging buttons. There are 32
 * Apply CTAs and only two of them pass any context today. Reading the
 * same-origin referrer covers all 32 at once with no edit to any of them, and
 * it cannot fall out of step when someone adds the 33rd. Tagging is still
 * useful for the one thing a referrer cannot do - telling two CTAs on the SAME
 * page apart - so `?cta=` is read when present and left null when not.
 */

const AD_CLICK_KEY = "ca_ad_click_id"

/** The click-id params we recognise, in the order we prefer them. */
const AD_CLICK_PARAMS = ["fbclid", "gclid", "ttclid", "msclkid"] as const

/**
 * Store the raw advertising click id once per browser, first one wins.
 *
 * `captureFirstTouch` already records that a click id was PRESENT, as the
 * "paid:meta" marker, and deliberately throws the value away. That was enough
 * to split paid from organic and not enough to trace a lead back to the ad that
 * produced it - the only durable copies were the ones handed to the Meta CAPI
 * outbox, which are scrubbed at event_time + 48 hours by design. The owner
 * asked for the id itself to be kept (2026-08-22).
 */
export function captureAdClickId(): void {
  if (typeof window === "undefined") return
  try {
    if (localStorage.getItem(AD_CLICK_KEY)) return
    const params = new URLSearchParams(window.location.search)
    for (const key of AD_CLICK_PARAMS) {
      const value = params.get(key)
      if (value && value.trim()) {
        localStorage.setItem(AD_CLICK_KEY, `${key}:${value.trim()}`.slice(0, 300))
        return
      }
    }
  } catch {
    // Private mode. The paid marker on answers.source still survives.
  }
}

export function readAdClickId(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return localStorage.getItem(AD_CLICK_KEY) || undefined
  } catch {
    return undefined
  }
}

/**
 * The pathname of the page the visitor came from, when it was one of ours.
 *
 * A cross-origin referrer is deliberately dropped rather than stored: that is
 * first-touch information, `answers.source` already holds it as a hostname, and
 * a full external URL can carry a query string we have no reason to keep.
 */
export function entryPageFromReferrer(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    if (!document.referrer) return undefined
    const url = new URL(document.referrer)
    if (url.hostname !== window.location.hostname) return undefined
    // A man who reloads /apply, or steps back and forward inside the form,
    // refers himself. That is not an entry page and would overwrite the real
    // one with noise.
    if (url.pathname === window.location.pathname) return undefined
    return url.pathname.slice(0, 200)
  } catch {
    return undefined
  }
}

/** The optional `?cta=` label, for telling two buttons on one page apart. */
export function entryCtaFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    const value = new URLSearchParams(window.location.search).get("cta")
    return value?.trim() ? value.trim().slice(0, 80) : undefined
  } catch {
    return undefined
  }
}

/**
 * The entry/experiment columns for a lead write, built once so the lead save
 * and the application save can never write a different set.
 *
 * A KEY IS OMITTED WHEN WE HAVE NO VALUE, never written as null. These land in
 * an UPDATE that runs on every step save, so a null would not mean "unknown" -
 * it would OVERWRITE what an earlier save already learned. The trigger is
 * ordinary rather than exotic: a man starts on /mentorship, abandons, and comes
 * back through the resume link in the abandoned-lead email. That second visit
 * has no same-origin referrer and carries ?resume= rather than ?cta=, so every
 * one of these reads as absent, and writing them would erase the entry page he
 * actually came from.
 *
 * This is the same shape as the bug that nulled a real E.164 phone number
 * milliseconds after the booking route stored it, leaving only 13 of 61 leads
 * with one. A column is never fed a null by a save that simply did not carry
 * the value.
 *
 * Lives here rather than in the route because a route handler is a thin
 * adapter, and because a rule this expensive to get wrong should be testable on
 * its own.
 */
export function entryColumns(input: {
  entry_page?: unknown
  entry_cta?: unknown
  ad_click_id?: unknown
  money_arm?: unknown
}): Record<string, string> {
  const out: Record<string, string> = {}
  const put = (key: string, value: unknown, cap: number) => {
    if (typeof value === "string" && value.trim()) out[key] = value.trim().slice(0, cap)
  }
  put("entry_page", input.entry_page, 200)
  put("entry_cta", input.entry_cta, 80)
  put("ad_click_id", input.ad_click_id, 300)
  if (input.money_arm === "price_disclosed" || input.money_arm === "budget_bands") {
    out.money_arm = input.money_arm
  }
  return out
}
